import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react"

import { toast } from "@/common/components/UI/Toast"
import { useRouter } from "@/common/navigation"
import { storage } from "@/common/services/localStorage.service"

const STORAGE_KEY = "nexus_generating_agents"
const POLL_INTERVAL = 5000 // 5 seconds

export interface GeneratingAgent {
  id: string
  firstName: string
  lastName: string
  role: string
  startedAt: number
}

interface GeneratingAgentsContextState {
  generatingAgents: GeneratingAgent[]
  addGeneratingAgent: (agent: Omit<GeneratingAgent, "startedAt">) => void
  removeGeneratingAgent: (id: string) => void
}

const GeneratingAgentsContext = createContext<GeneratingAgentsContextState | undefined>(undefined)

export function useGeneratingAgents(): GeneratingAgentsContextState {
  const context = useContext(GeneratingAgentsContext)
  if (!context) {
    throw new Error("useGeneratingAgents must be used within a GeneratingAgentsProvider")
  }
  return context
}

export function GeneratingAgentsProvider({ children }: PropsWithChildren) {
  const router = useRouter()
  const [generatingAgents, setGeneratingAgents] = useState<GeneratingAgent[]>([])

  // Load from localStorage on mount
  useEffect(() => {
    const parsed = storage.get<GeneratingAgent[]>(STORAGE_KEY)
    if (parsed && Array.isArray(parsed)) {
      // Filter out agents that have been generating for more than 10 minutes (probably failed)
      const tenMinutesAgo = Date.now() - 10 * 60 * 1000
      const validAgents = parsed.filter((agent) => agent.startedAt > tenMinutesAgo)
      setGeneratingAgents(validAgents)
      // Update storage with filtered list
      if (validAgents.length !== parsed.length) {
        storage.set(STORAGE_KEY, validAgents)
      }
    }
  }, [])

  // Persist to localStorage whenever generatingAgents changes
  useEffect(() => {
    storage.set(STORAGE_KEY, generatingAgents)
  }, [generatingAgents])

  // Poll API to check completion status
  useEffect(() => {
    if (generatingAgents.length === 0) return

    const checkStatus = async () => {
      for (const agent of generatingAgents) {
        try {
          const response = await fetch(`/api/agents/assistant/agent-status/${agent.id}`)
          if (!response.ok) continue

          const data = await response.json()

          if (data.status === "ready") {
            // Agent is ready! Remove from generating list and show toast
            setGeneratingAgents((prev) => prev.filter((a) => a.id !== agent.id))

            const agentName = `${agent.firstName} ${agent.lastName}`.trim() || "Your agent"
            toast.success(`${agentName} is ready!`)
          } else if (data.status === "failed") {
            // Agent generation failed
            setGeneratingAgents((prev) => prev.filter((a) => a.id !== agent.id))

            const agentName = `${agent.firstName} ${agent.lastName}`.trim() || "Agent"
            toast.error(`${agentName} generation failed. Please try again.`)
          }
        } catch {
          // Ignore fetch errors, will retry on next poll
        }
      }
    }

    // Check immediately and then poll
    checkStatus()
    const interval = setInterval(checkStatus, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [generatingAgents, router])

  const addGeneratingAgent = useCallback((agent: Omit<GeneratingAgent, "startedAt">) => {
    setGeneratingAgents((prev) => {
      // Don't add if already exists
      if (prev.some((a) => a.id === agent.id)) return prev
      return [{ ...agent, startedAt: Date.now() }, ...prev]
    })
  }, [])

  const removeGeneratingAgent = useCallback((id: string) => {
    setGeneratingAgents((prev) => prev.filter((a) => a.id !== id))
  }, [])

  return (
    <GeneratingAgentsContext.Provider
      value={{
        generatingAgents,
        addGeneratingAgent,
        removeGeneratingAgent
      }}
    >
      {children}
    </GeneratingAgentsContext.Provider>
  )
}
